import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Container, Row, Col, Button, Offcanvas } from "react-bootstrap";
import FilterSidebar from "../components/FilterSidebar";
import ResultsHeader from "../components/ResultsHeader";
import ResultsPagination from "../components/ResultsPagination";
import { ChevronRight } from "lucide-react";
import TenderCard from "../components/TenderCard";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SearchFilter from "../components/SearchFilter";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import PageSkeleton from "../components/skeletons/PageSkeleton";

const API_BASE_URL = `${process.env.REACT_APP_BASE_API_URL}`;

const initialFilters = {
  state: [],
  city: [],
  ministry: [],
  department: [],
  category: [],
  organization: [],
  status: [],
  deadline: [],
  eligibility: [],
  minValue: null,
  emdValue: null,
  startDate: "",
  endDate: ""
};

const ARRAY_FILTER_KEYS = [
  "state",
  "city",
  "ministry",
  "department",
  "category",
  "organization",
  "status",
  "deadline",
  "eligibility"
];

const parseFiltersFromParams = (params) => {
  const filters = { ...initialFilters };
  ARRAY_FILTER_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) filters[key] = val.split(",").filter(Boolean);
  });
  const minValue = params.get("minValue");
  if (minValue) filters.minValue = Number(minValue);
  const emdValue = params.get("emdValue");
  if (emdValue) filters.emdValue = Number(emdValue);
  filters.startDate = params.get("startDate") || "";
  filters.endDate = params.get("endDate") || "";
  return filters;
};

const filtersToParams = (filters) => {
  const out = {};
  ARRAY_FILTER_KEYS.forEach((key) => {
    if (Array.isArray(filters[key]) && filters[key].length > 0) {
      out[key] = filters[key].join(",");
    }
  });
  if (filters.minValue) out.minValue = String(filters.minValue);
  if (filters.emdValue) out.emdValue = String(filters.emdValue);
  if (filters.startDate) out.startDate = filters.startDate;
  if (filters.endDate) out.endDate = filters.endDate;
  return out;
};

const mapTenderData = (t) => {
  const formatCurrency = (value) => {
    if (!value || Number(value) <= 0) return "N/A";
    if (Number(value) < 1000) return "N/A";
    return `₹ ${Number(value).toLocaleString("en-IN")}`;
  };

  const tenderValStr = formatCurrency(t.tender_value || t.estimated_value);
  const emdValStr = formatCurrency(t.emd_value || t.emd_amount);

  const id = t.tender_id || t.id || t.gem_ra_number || t.gem_bid_parent_id;

  return {
    ...t,
    id: id,
    tender_id: t.tender_id || t.gem_ra_number || t.gem_bid_parent_id || id,
    title: t.title || t.project_name || t.work_name || "N/A",
    referenceNo: t.tender_id || t.gem_ra_number || "N/A",
    endDate: t.award_date || t.submission_deadline,
    organization: t.organization || t.organization_name || "N/A",
    organisationName: t.organization || t.organization_name || "N/A",
    department: t.department || t.department_name || "N/A",
    ministry: t.ministry || t.ministry_name || "N/A",
    ministryName: t.ministry || t.ministry_name || "N/A",
    location: t.location || "N/A",
    city: t.location || "N/A",
    cityName: t.location || "N/A",
    state: "",
    stateName: "",
    bidValue: tenderValStr,
    estimatedBidValue: tenderValStr,
    tenderAmount: tenderValStr,
    emdValue: emdValStr,
    emd: emdValStr,
    emdAmount: emdValStr,
    status: "Awarded",
    daysLeft: t.days_left ?? "0",
    mseExemption: t.mse_exemption === "Yes" ? "Yes" : "-",
    startupExemption: t.startup_exemption === "Yes" ? "Yes" : "-",
    display_name: t.title || "N/A",
    effective_closing_date: t.award_date || t.submission_deadline,
    display_organization: t.organization || t.organization_name || "N/A",
    display_department: t.department || t.department_name || "N/A",
    display_ministry: t.ministry || t.ministry_name || "N/A",
    display_location: t.location || "N/A",
    display_value: tenderValStr,
    display_emd: emdValStr,
    text_preview: t.text_preview || "",
    links_count: t.links_count || 0,
    gem_ra_number: t.gem_ra_number,
    gem_bid_parent_id: t.gem_bid_parent_id,
    views: t.view_count || t.views || 0,
    applies: t.apply_count || t.applies || 0,
    favourites_count: t.favourites_count || 0,
    isFavourite: t.isFavourite || false,
    l1_winner: t.l1_winner || "N/A",
    evaluation_count: t.evaluation_count || 0,
    l1_price_display: t.l1_price_display || formatCurrency(t.l1_price),
    award_date: t.award_date || "N/A"
  };
};

const AwardedTenders = ({ onToggleFavourite, openAuthModal }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const clearAwardedStorage = useCallback(() => {
    const keys = [
      "aw_searchValue",
      "aw_page",
      "aw_perPage",
      "aw_sortOption",
      "aw_filters",
      "searchFormState",
      "pendingSearchFilters"
    ];
    keys.forEach((key) => sessionStorage.removeItem(key));
  }, []);

  const isComingFromHome = useMemo(() => {
    if (
      location.state?.fromActiveTenders === true ||
      location.state?.clearSearch === true
    ) {
      return true;
    }
    return false;
  }, [location.state]);

  useEffect(() => {
    if (isComingFromHome) {
      clearAwardedStorage();
      const params = new URLSearchParams(location.search);
      const keys = [
        "search",
        "page",
        "perPage",
        "sort",
        "organization",
        "category",
        "state",
        "city",
        "ministry",
        "department",
        "status",
        "deadline",
        "eligibility",
        "minValue",
        "emdValue",
        "startDate",
        "endDate"
      ];
      let hasParams = false;
      keys.forEach((key) => {
        if (params.has(key)) {
          hasParams = true;
          params.delete(key);
        }
      });
      if (hasParams) {
        const newUrl = `${location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [isComingFromHome, clearAwardedStorage, location]);

  const [showFilters, setShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => searchParams.get("search") || ""
  );
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOption, setSortOption] = useState(
    () => searchParams.get("sort") || "award_date_desc"
  );
  const [page, setPage] = useState(
    () => Number(searchParams.get("page")) || 1
  );
  const [perPage, setPerPage] = useState(
    () => Number(searchParams.get("perPage")) || 10
  );
  const [sortOptions] = useState([
    { label: "Latest Awarded", value: "award_date_desc" },
    { label: "Oldest Awarded ", value: "award_date_asc" },
    { label: "Highest Value", value: "highest_value" },
    { label: "Lowest EMD", value: "lowest_emd" },
    { label: "Favourites", value: "favourites" }
  ]);
  const [perPageOptions, setPerPageOptions] = useState([10, 15, 20, 25, 30]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(() =>
    parseFiltersFromParams(searchParams)
  );

  const [prefetchedTenders, setPrefetchedTenders] = useState({});
  const prefetchTimeoutRef = useRef(null);
  const abortPrefetchRef = useRef(null);

  const isFirstFiltersEffect = useRef(true);
  const isFetchingRef = useRef(false);

  const goHome = useCallback(() => {
    clearAwardedStorage();
    navigate("/", {
      state: { fromActiveTenders: true, clearSearch: true },
      replace: true
    });
  }, [clearAwardedStorage, navigate]);

  const prefetchTenderDetails = useCallback(
    async (tenderId) => {
      if (prefetchedTenders[tenderId]) return;
      if (abortPrefetchRef.current) abortPrefetchRef.current.abort();
      const controller = new AbortController();
      abortPrefetchRef.current = controller;
      try {
        const userId = user?.id || user?._id;
        const params = userId ? { userId } : {};
        const response = await axios.get(
          `${API_BASE_URL}/api/tenders/${tenderId}`,
          { params }
        );
        setPrefetchedTenders((prev) => ({ ...prev, [tenderId]: response.data }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.debug("Prefetch failed for", tenderId);
        }
      }
    },
    [user, prefetchedTenders]
  );

  const handleTenderHover = useCallback(
    (tenderId) => {
      if (prefetchTimeoutRef.current) clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = setTimeout(() => {
        prefetchTenderDetails(tenderId);
      }, 300);
    },
    [prefetchTenderDetails]
  );

  const fetchTenders = useCallback(
    async (fetchPage) => {
      if (isFetchingRef.current) return;
      try {
        setLoading(true);
        isFetchingRef.current = true;
        const currentPage = fetchPage || page;
        const params = {
          page: currentPage,
          limit: perPage,
          search: debouncedSearch,
          sort: sortOption,
          userId: user?.id || user?._id,
          ...(filters?.startDate ? { startDate: filters.startDate } : {}),
          ...(filters?.endDate ? { endDate: filters.endDate } : {})
        };

        const res = await axios.get(`${API_BASE_URL}/api/awarded-tenders`, {
          params
        });

        const mappedTenders = (res.data?.tenders || []).map(mapTenderData);

        setTenders(mappedTenders);
        setTotal(res.data?.total || 0);
        setTotalPages(res.data?.totalPages || 1);
        setPerPageOptions(res.data?.pageOptions || [10, 15, 20, 25, 30]);

        if (fetchPage && fetchPage !== page) setPage(fetchPage);
        setLoading(false);
      } catch (err) {
        console.error("Awarded fetch error:", err);
        setTenders([]);
        setTotal(0);
        setTotalPages(1);
        setLoading(false);
      } finally {
        isFetchingRef.current = false;
      }
    },
    [
      debouncedSearch,
      page,
      perPage,
      sortOption,
      user?.id,
      user?._id,
      filters
    ]
  );

  const syncUrl = useCallback(
    (overrides = {}) => {
      const next = {
        page: overrides.page !== undefined ? overrides.page : page,
        perPage: overrides.perPage !== undefined ? overrides.perPage : perPage,
        sort: overrides.sort !== undefined ? overrides.sort : sortOption,
        search:
          overrides.search !== undefined ? overrides.search : debouncedSearch,
        filters: overrides.filters !== undefined ? overrides.filters : filters
      };

      const params = new URLSearchParams();
      if (next.search) params.set("search", next.search);
      if (next.page && next.page !== 1) params.set("page", String(next.page));
      if (next.perPage && next.perPage !== 10)
        params.set("perPage", String(next.perPage));
      if (next.sort && next.sort !== "award_date_desc")
        params.set("sort", next.sort);

      const filterParams = filtersToParams(next.filters);
      Object.entries(filterParams).forEach(([k, v]) => params.set(k, v));

      const newSearch = params.toString();
      const currentSearch = location.search.startsWith("?")
        ? location.search.slice(1)
        : location.search;
      if (newSearch !== currentSearch) {
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
      }
    },
    [
      page,
      perPage,
      sortOption,
      debouncedSearch,
      filters,
      navigate,
      location.pathname,
      location.search
    ]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage === page) return;
      if (newPage < 1 || newPage > totalPages) return;
      setPage(newPage);
      sessionStorage.setItem("aw_page", String(newPage));
      syncUrl({ page: newPage });
      fetchTenders(newPage);
    },
    [page, totalPages, fetchTenders, syncUrl]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    if (isFirstFiltersEffect.current) {
      isFirstFiltersEffect.current = false;
      fetchTenders(page);
      return;
    }
    setPage(1);
    sessionStorage.setItem("aw_page", "1");
    fetchTenders(1);
  }, [debouncedSearch, perPage, sortOption, filters]);

  useEffect(() => {
    if (!isFirstFiltersEffect.current) {
      syncUrl();
    }
  }, [page, perPage, sortOption, debouncedSearch, filters]);

  useEffect(() => {
    sessionStorage.setItem("aw_page", String(page));
    sessionStorage.setItem("aw_perPage", String(perPage));
    sessionStorage.setItem("aw_sortOption", sortOption);
    sessionStorage.setItem("aw_searchValue", searchValue);
    sessionStorage.setItem("aw_filters", JSON.stringify(filters));
  }, [page, perPage, sortOption, searchValue, filters]);

  useEffect(() => {
    return () => {
      if (prefetchTimeoutRef.current) clearTimeout(prefetchTimeoutRef.current);
      if (abortPrefetchRef.current) abortPrefetchRef.current.abort();
    };
  }, []);

  const handleResetFilters = () => {
    setFilters(initialFilters);
  };

  const handleToggleFavourite = useCallback((tenderId, isFavourite) => {
    setTenders((prev) =>
      prev.map((t) =>
        String(t.tender_id || t.id || t._id) === String(tenderId)
          ? { ...t, isFavourite }
          : t
      )
    );
  }, []);

  const buildReturnUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (page && page !== 1) params.set("page", String(page));
    if (perPage && perPage !== 10) params.set("perPage", String(perPage));
    if (sortOption && sortOption !== "award_date_desc")
      params.set("sort", sortOption);

    const filterParams = filtersToParams(filters);
    Object.entries(filterParams).forEach(([k, v]) => params.set(k, v));

    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  }, [debouncedSearch, page, perPage, sortOption, filters, location.pathname]);

  const handleTenderOpen = useCallback(
    (tenderId) => {
      const returnTo = encodeURIComponent(buildReturnUrl());
      const w = window.open(
        `/Tender-Awarded/${tenderId}?returnTo=${returnTo}`,
        "_blank"
      );
      if (w) {
        try {
          w.opener = window;
        } catch (e) {}
      }
    },
    [buildReturnUrl]
  );

  const appliedCount = useMemo(() => {
    return (
      (filters?.organization?.length || 0) +
      (filters?.state?.length || 0) +
      (filters?.city?.length || 0) +
      (filters?.ministry?.length || 0) +
      (filters?.department?.length || 0) +
      (filters?.category?.length || 0) +
      (filters?.deadline?.length || 0) +
      (filters?.status?.length || 0) +
      (filters?.eligibility?.length || 0)
    );
  }, [filters]);

  return (
    <>
      <nav
        aria-label="breadcrumb"
        style={{
          "--bs-breadcrumb-divider": "''",
          borderColor: "rgba(229, 231, 235, 1)"
        }}
      >
        <ol
          className="breadcrumb d-flex align-items-center mb-0"
          style={{ fontSize: "14px" }}
        >
          <li className="breadcrumb-item d-flex align-items-center">
            <button
              onClick={goHome}
              className="text-decoration-none text-secondary"
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Home
            </button>
            <ChevronRight size={16} className="mx-1 text-secondary" />
          </li>
          <li
            className="breadcrumb-item fw-semibold"
            style={{ color: "#2F7A87" }}
          >
            Awarded Tenders
          </li>
        </ol>
      </nav>

      <Container fluid className="py-4">
        <SearchFilter
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          startDate={filters.startDate}
          setStartDate={(val) =>
            setFilters((prev) => ({ ...prev, startDate: val }))
          }
          endDate={filters.endDate}
          setEndDate={(val) =>
            setFilters((prev) => ({ ...prev, endDate: val }))
          }
        />

        <Row>
          <Col className="d-lg-none mb-3">
            <Button
              variant="outline-secondary"
              onClick={() => setShowFilters(true)}
            >
              All Filters
            </Button>
          </Col>

          <Col
            lg={3}
            className="d-none d-lg-block sidebar-scroll-column"
            style={{
              height: "calc(100vh - 140px)",
              overflowY: "auto",
              paddingRight: "10px",
              scrollbarWidth: "thin"
            }}
          >
            <FilterSidebar
              filters={filters}
              setFilters={setFilters}
              type="awarded"
            />
          </Col>

          <Col
            lg={9}
            style={{
              height: "calc(100vh - 140px)",
              overflowY: "auto",
              paddingRight: "15px",
              paddingBottom: "30px",
              scrollbarWidth: "thin"
            }}
            className="tenders-scroll-column"
          >
            <ResultsHeader
              searchKeyword={searchValue}
              perPage={perPage}
              setPerPage={setPerPage}
              setActivePage={handlePageChange}
              activePage={page}
              totalPages={totalPages}
              total={total}
              sortOption={sortOption}
              setSortOption={setSortOption}
              sortOptions={sortOptions}
              perPageOptions={perPageOptions}
            />

            {loading ? (
              <PageSkeleton />
            ) : tenders.length > 0 ? (
              tenders.map((tender) => (
                <TenderCard
                  key={tender.id || tender._id || tender.gem_ra_number}
                  tender={tender}
                  mode="result"
                  onToggleFavourite={handleToggleFavourite}
                  openAuthModal={openAuthModal}
                  searchValue={searchValue}
                  onMouseEnter={() => handleTenderHover(tender.id)}
                  prefetchedData={prefetchedTenders[tender.id]}
                  onTenderClick={handleTenderOpen}
                />
              ))
            ) : (
              <div className="px-4 py-3 text-muted">
                No awarded tenders found.
              </div>
            )}

            <ResultsPagination
              activePage={page}
              setActivePage={handlePageChange}
              perPage={perPage}
              setPerPage={setPerPage}
              totalPages={totalPages}
              perPageOptions={perPageOptions}
            />
          </Col>
        </Row>

        <Offcanvas
          show={showFilters}
          onHide={() => setShowFilters(false)}
          placement="start"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="d-flex justify-content-between align-items-center w-100">
              <span>All Filters</span>
              <Button
                variant="light"
                size="sm"
                onClick={handleResetFilters}
                aria-label="Reset Filters"
                title="Reset Filters"
              >
                <img
                  src="/images/filter.svg"
                  alt="Reset"
                  style={{ width: "16px", height: "16px" }}
                />
              </Button>
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <FilterSidebar
              filters={filters}
              setFilters={setFilters}
              type="awarded"
            />
          </Offcanvas.Body>
        </Offcanvas>
      </Container>
    </>
  );
};

export default AwardedTenders;